import { Routes } from '@angular/router';
import { pagesRoutes } from './pages/pages.routes';
import { settingsRoutes } from './settings/settings.routes';
import { authGuard, checkAbandoneOrNoAddressdUser, maintenanceGuard, checkMembershipPermissions, maintenanceOffGuard, preventForAbandoneOrNoAddressdUser, signupCompletedGuard } from './shared/guards/auth-guard.guard';
import { HomeComponent } from './home/home.component';
import { ShopComponent } from './shop/shop.component';
import { accountRecoveryRoutes } from './authentication/account-recovery/account-recovery.routes';
import { canDeactivateGuard } from './shared/guards/deactivate/can-deactivate.guard';
import { productResolver } from './resolvers/product.resolver';
import { shopResolver } from './resolvers/shop.resolver';

// Lazy chunks — keep everything but the entry routes out of the initial bundle.
//
// HomeComponent is eager because `/` is the entry route for an anonymous visitor; any
// loadComponent delay there is visible as a blank flash on first visit. ShopComponent is eager
// for the same reason on the other side of the door: `/shop` is where a signed-in customer
// lands (checkMembershipPermissions makes it their defaultRoute, and signupCompletedGuard sends
// them there), so the rule that kept `/` eager applies to it too — DEV-10320 moved it to
// loadComponent without that carve-out. Locavore, which never took that commit, still serves
// the shop eagerly.
//
// It also costs little: `/shop` is the most visited route in the app, so its chunk is fetched
// in nearly every session anyway.
const loadShopOrProductComponent = () =>
  import('./shop/shop-or-product/shop-or-product.component').then(m => m.ShopOrProductComponent);

export const routes: Routes = [
  {
    path: 'maintenance',
    title: 'Maintenance',
    loadComponent: () => import('./maintenance/maintenance.component').then(m => m.MaintenanceComponent),
    canActivate: [maintenanceOffGuard],
    data: { preload: false }
  },
  {
    path: 'ecommerce-admin/:token',
    title: 'Ecommerce Admin',
    loadComponent: () => import('./authentication/ecommerce-admin/ecommerce-admin.component').then(m => m.EcommerceAdminComponent),
    canActivate: [maintenanceGuard],
    data: { preload: false }
  },
  {
    path: '',
    component: HomeComponent,
    canActivate: [maintenanceGuard]
  },
  {
    path: 'login',
    title: 'Log In',
    loadComponent: () => import('./authentication/login/login.component').then(m => m.LoginComponent),
    canActivate: [maintenanceGuard, authGuard]
  },
  {
    path: 'signup',
    title: 'Sign Up',
    loadComponent: () => import('./authentication/signup-v2/signup-v2.component').then(m => m.SignupV2Component),
    canActivate: [maintenanceGuard, signupCompletedGuard]
  },
  ...pagesRoutes,
  ...settingsRoutes,
  ...accountRecoveryRoutes,
  {
    path: 'search',
    redirectTo: 'shop',
    pathMatch: 'full'
  },
  {
    path: 'shop',
    component: ShopComponent,
    canActivate: [maintenanceGuard, checkMembershipPermissions, checkAbandoneOrNoAddressdUser],
    canActivateChild: [maintenanceGuard],
    resolve: { shop: shopResolver }
  },
  {
    path: 'shop/:category',
    component: ShopComponent,
    canActivate: [maintenanceGuard, checkMembershipPermissions, checkAbandoneOrNoAddressdUser],
    resolve: { shop: shopResolver }
  },
  {
    path: 'shop/:category/:subcategory',
    component: ShopComponent,
    canActivate: [maintenanceGuard, checkMembershipPermissions, checkAbandoneOrNoAddressdUser],
    resolve: { shop: shopResolver }
  },
  {
    path: 'shop/:category/:subcategory/:slug',
    loadComponent: loadShopOrProductComponent,
    canActivate: [maintenanceGuard, checkMembershipPermissions],
    resolve: { product: productResolver },
    // Force the resolver to re-run on every navigation to this route, including
    // PDP→PDP jumps from the search bar. Without this, Angular reuses the resolved
    // data when only the params change and the inner ProductComponent keeps painting
    // the previous product.
    runGuardsAndResolvers: 'always'
  },
  {
    path: 'favorites',
    title: 'Favorites',
    loadComponent: () => import('./fav-products/fav-products.component').then(m => m.FavProductsComponent),
    canActivate: [maintenanceGuard, checkMembershipPermissions]

  },
  {
    path: 'buy-again',
    title: 'Buy again',
    loadComponent: () => import('./buy-again-products/buy-again-products.component').then(m => m.BuyAgainProductsComponent),
    canActivate: [maintenanceGuard, checkMembershipPermissions]

  },
  {
    path: 'order',
    title: 'Order',
    loadComponent: () => import('./order/order.component').then(m => m.OrderComponent),
    canActivate: [maintenanceGuard, checkMembershipPermissions, preventForAbandoneOrNoAddressdUser],
    canDeactivate: [canDeactivateGuard]
  },
  {
    path: 'cart',
    title: 'Cart',
    loadComponent: () => import('./cart-preview/cart-preview.component').then(m => m.CartPreviewComponent),
    canActivate: [maintenanceGuard, checkMembershipPermissions]
  },
  {
    path: 'terms-and-conditions',
    title: 'Terms & Conditions',
    loadComponent: () => import('./authentication/signup/terms-and-conditions/terms-and-conditions.component').then(m => m.TermsAndConditionsComponent),
    canActivate: [maintenanceGuard],
    data: { preload: false }
  },
  { path: '**', redirectTo: '' },
];
